import { useMemo, useState } from 'react'
import type { EChartsCoreOption } from 'echarts/core'
import { formatMoney, formatQuantity, type Holding } from '@greencandle/api-client'
import { useECharts } from './echarts'
import { useChartTheme } from './theme'
import { compactMoney } from './format'

/**
 * FR-H10 groundwork: what the option book looks like, and when it rolls off.
 *
 * <p>Deliberately not a value chart. Neither price provider quotes options (FR-D6 is a later
 * phase), so every option holding comes back with a null market value, and a chart of "value by
 * expiry" would be a chart of nothing. What is known exactly is the contract - underlying,
 * strike, expiry, right, multiplier - and what it cost.
 *
 * <p>So this plots **notional at risk by expiry**: strike x multiplier x contracts, long above
 * the axis and short below. That is the number that says how much of the underlying the book is
 * committed to and when that commitment expires, and it needs no price at all.
 *
 * <p>It is not delta-adjusted. A far out-of-the-money contract counts its full notional here
 * while behaving like a fraction of it, which is exactly what FR-E9 exists to fix later. The
 * chart says so on its face rather than letting the number be read as exposure.
 */
export function OptionLadder({ holdings, currency }: { holdings: Holding[]; currency: string }) {
  const theme = useChartTheme()
  const [showTable, setShowTable] = useState(false)

  const rows = useMemo(() => holdings.filter((h) => h.assetClass === 'OPTION'), [holdings])

  // Grouped by expiry, which is the axis the book actually rolls off along. The contract
  // comes from the server on the holding itself - it is not recovered from the symbol here.
  // Reading the OCC string back was a second implementation of the importer\'s own grammar,
  // and it assumed every contract is worth 100 of the underlying, which is the usual case
  // and not the rule.
  const byExpiry = useMemo(() => {
    const map = new Map<string, { long: number; short: number; contracts: number }>()
    for (const row of rows) {
      const contract = row.option
      if (!contract) continue
      const qty = Number(row.quantity)
      const notional = Number(contract.strike) * Number(contract.multiplier) * Math.abs(qty)
      const entry = map.get(contract.expiry) ?? { long: 0, short: 0, contracts: 0 }
      if (qty >= 0) entry.long += notional
      else entry.short -= notional
      entry.contracts += qty
      map.set(contract.expiry, entry)
    }
    return [...map.entries()].sort((a, b) => a[0].localeCompare(b[0]))
  }, [rows])

  const option = useMemo<EChartsCoreOption | null>(() => {
    if (byExpiry.length === 0) return null

    return {
      animation: false,
      grid: { left: 8, right: 16, top: 24, bottom: 8, containLabel: true },
      xAxis: {
        type: 'category',
        data: byExpiry.map(([expiry]) => expiry),
        axisLine: { lineStyle: { color: theme.axis, width: 1 } },
        axisTick: { show: false },
        axisLabel: { color: theme.textMuted, hideOverlap: true },
      },
      yAxis: {
        type: 'value',
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: { color: theme.textMuted, formatter: (v: number) => compactMoney(v, currency) },
        splitLine: { lineStyle: { color: theme.grid, width: 1, type: 'solid' } },
      },
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        backgroundColor: theme.surface,
        borderColor: theme.grid,
        textStyle: { color: theme.text },
        formatter: (params: unknown) => {
          const list = Array.isArray(params) ? params : [params]
          const first = list[0] as { axisValue?: string } | undefined
          if (!first?.axisValue) return ''
          const entry = byExpiry.find(([e]) => e === first.axisValue)?.[1]
          if (!entry) return ''

          const root = document.createElement('div')
          const head = document.createElement('div')
          head.style.cssText = `color:${theme.textMuted};font-size:12px;margin-bottom:4px`
          head.textContent = `Expires ${first.axisValue}`
          root.append(head)

          for (const [label, value] of [
            ['Long notional', entry.long],
            ['Short notional', entry.short],
          ] as const) {
            if (value === 0) continue
            const line = document.createElement('div')
            line.style.cssText = 'font-weight:600'
            line.textContent = `${label}: ${formatMoney(String(value), currency) ?? ''}`
            root.append(line)
          }
          const net = document.createElement('div')
          net.style.cssText = `color:${theme.textMuted};font-size:12px;margin-top:4px`
          net.textContent = `${entry.contracts >= 0 ? '+' : ''}${entry.contracts} net contracts`
          root.append(net)
          return root
        },
      },
      series: [
        {
          // Long and short are polarity, not identity, so they take the two poles of a
          // diverging pair rather than two categorical slots.
          name: 'Long',
          type: 'bar',
          stack: 'notional',
          barMaxWidth: 24,
          data: byExpiry.map(([, v]) => v.long),
          itemStyle: {
            color: theme.series[0],
            borderColor: theme.surface,
            borderWidth: 1,
            borderRadius: [4, 4, 0, 0],
          },
        },
        {
          name: 'Short',
          type: 'bar',
          stack: 'notional',
          barMaxWidth: 24,
          data: byExpiry.map(([, v]) => v.short),
          itemStyle: {
            color: theme.series[7],
            borderColor: theme.surface,
            borderWidth: 1,
            borderRadius: [0, 0, 4, 4],
          },
        },
      ],
    }
  }, [byExpiry, currency, theme])

  const container = useECharts(option)

  if (rows.length === 0) {
    return null
  }

  return (
    <div className="card" style={{ marginTop: 16 }}>
      <div className="chart-head">
        <h2 className="card-title">Options · notional by expiry</h2>
        <div className="chart-head-end">
          <button type="button" onClick={() => setShowTable((v) => !v)} aria-pressed={showTable}>
            {showTable ? 'Chart' : 'Table'}
          </button>
        </div>
      </div>

      {showTable ? (
        <OptionTable rows={rows} currency={currency} />
      ) : (
        <>
          <div ref={container} style={{ width: '100%', height: 240 }} />
          <div className="legend">
            <span className="legend-item">
              <span className="legend-swatch" style={{ background: 'var(--series-1)' }} />
              Long
            </span>
            <span className="legend-item">
              <span className="legend-swatch" style={{ background: 'var(--series-8)' }} />
              Short
            </span>
          </div>
        </>
      )}

      {/*
        Said on the chart, not buried in a tooltip. Notional is not exposure, and an option book
        shown without prices is a book shown at cost.
      */}
      <p className="chart-note warn">
        Notional (strike x contract size), not delta-adjusted and not market value — no price
        source quotes options yet, so these positions carry cost only.
      </p>
    </div>
  )
}

function OptionTable({ rows, currency }: { rows: Holding[]; currency: string }) {
  const parsed = rows
    .map((row) => ({ row, contract: row.option }))
    .sort((a, b) => (a.contract?.expiry ?? '').localeCompare(b.contract?.expiry ?? ''))

  return (
    <div className="table-scroll" style={{ maxHeight: 340, overflowY: 'auto' }}>
      <table>
        <thead>
          <tr>
            <th>Underlying</th>
            <th>Expiry</th>
            <th className="right">Strike</th>
            <th>Right</th>
            <th className="right">Contracts</th>
            <th className="right">Cost basis</th>
            <th className="right">Notional</th>
          </tr>
        </thead>
        <tbody>
          {parsed.map(({ row, contract }) => {
            const qty = Number(row.quantity)
            return (
              <tr key={row.instrumentId}>
                <td>{contract?.underlyingSymbol ?? row.symbol}</td>
                <td className="num">{contract?.expiry ?? '—'}</td>
                <td className="right num">{contract ? contract.strike : '—'}</td>
                <td className={contract?.right === 'CALL' ? 'up' : 'down'}>
                  {contract?.right ?? '—'}
                </td>
                <td className={`right num ${qty >= 0 ? 'up' : 'down'}`}>
                  {formatQuantity(row.quantity)}
                </td>
                <td className="right num">{formatMoney(row.costBasis, currency)}</td>
                <td className="right num">
                  {contract
                    ? formatMoney(
                        String(
                          Number(contract.strike) * Number(contract.multiplier) * Math.abs(qty),
                        ),
                        currency,
                      )
                    : '—'}
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

